import { Capacitor } from '@capacitor/core'
import { AppLauncher } from '@capacitor/app-launcher'
import { apiService } from '@/services/apiService'
import { APP_VERSION_CODE } from '@/utils/appVersion'

export interface AppVersionPolicy {
  minVersionCode: number
  latestVersionCode: number
  storeUrl: string
  message: string
}

export const PLAY_PACKAGE_ID = 'com.mastermind.coaching'

/** True when this installed build is older than the minimum the server asks for. */
export const isUpdateRequired = (policy: Pick<AppVersionPolicy, 'minVersionCode'> | null | undefined, installed = APP_VERSION_CODE) =>
  !!policy && policy.minVersionCode > 0 && installed < policy.minVersionCode

/**
 * Asks the server whether this build must be updated. Only the Android app is ever blocked, and any failure
 * (offline, server asleep) means "do not block": a network hiccup must never lock a parent out.
 */
export const fetchRequiredUpdate = async (installed = APP_VERSION_CODE): Promise<AppVersionPolicy | null> => {
  if (!Capacitor.isNativePlatform()) return null
  try {
    const response = await apiService.get('/public/app-version', { timeout: 8000 })
    const policy = (response?.data ?? response) as AppVersionPolicy
    return isUpdateRequired(policy, installed) ? policy : null
  } catch {
    return null
  }
}

export const openStoreListing = async (storeUrl: string): Promise<void> => {
  const attempts = [`market://details?id=${PLAY_PACKAGE_ID}`, storeUrl]
  for (const url of attempts) {
    try {
      const result = await AppLauncher.openUrl({ url })
      if (result?.completed !== false) return
    } catch {
      // try the next link
    }
  }
}
